import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Card, Form, Input, Button, notification, Space, Typography,
  DatePicker, Select, Switch, Row, Col, Alert,
} from 'antd';
import {
  SaveOutlined, ArrowLeftOutlined, CameraOutlined,
  LoadingOutlined, DeleteOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { baseUrl } from '../../config';

const { Title, Text } = Typography;
const { TextArea } = Input;

const token = () => localStorage.getItem('accessToken');
const authH = () => ({ Authorization: `Bearer ${token()}` });

export default function PortalUpdatesForm() {
  const history = useHistory();
  const { id }  = useParams();
  const isEdit  = Boolean(id);
  const [form]  = Form.useForm();

  const [saving,     setSaving]     = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [categories, setCategories] = useState([]);
  const [imageUrl,   setImageUrl]   = useState(null);
  const [uploading,  setUploading]  = useState(false);
  const [preview,    setPreview]    = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    loadCategories();
    if (isEdit) loadItem();
  }, [id]); // eslint-disable-line

  const loadCategories = async () => {
    try {
      const r = await axios.get(`${baseUrl}/portal-categories`, { headers: authH() });
      if (r.data.status) setCategories(r.data.result);
    } catch {}
  };

  const loadItem = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/portal-updates/${id}`, { headers: authH() });
      if (res.data.status) {
        const r = res.data.result;
        form.setFieldsValue({
          title:        r.title,
          category_id:  r.category_id ? String(r.category_id) : undefined,
          content:      r.content      || '',
          post_date:    r.post_date    ? moment(r.post_date) : null,
          is_published: r.is_published === 1 || r.is_published === true,
        });
        setImageUrl(r.image_url || null);
        setPreview(r.image_url  || null);
      }
    } catch { notification.error({ message: 'Load failed' }); }
    finally { setLoading(false); }
  };

  const handleImageFile = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp|gif)$/i.test(file.type)) {
      notification.warning({ message: 'JPG, PNG या WebP चुनें' }); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      notification.warning({ message: 'Image 10MB से कम होनी चाहिए' }); return;
    }
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res  = await fetch(`${baseUrl}/portal-updates/upload-image`, { method: 'POST', headers: authH(), body: fd });
      const data = await res.json();
      if (data.status) { setImageUrl(data.url); notification.success({ message: 'Image uploaded!' }); }
      else throw new Error(data.message);
    } catch (err) {
      notification.error({ message: 'Upload failed: ' + err.message });
      setPreview(null);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        title:        values.title,
        category_id:  values.category_id ? Number(values.category_id) : null,
        content:      values.content      || null,
        image_url:    imageUrl            || null,
        post_date:    values.post_date?.format('YYYY-MM-DD') || null,
        is_published: values.is_published ? 1 : 0,
      };
      if (isEdit) {
        await axios.patch(`${baseUrl}/portal-updates/${id}`, payload, { headers: authH() });
        notification.success({ message: 'Update saved!' });
      } else {
        await axios.post(`${baseUrl}/portal-updates`, payload, { headers: authH() });
        notification.success({ message: 'Update published!' });
      }
      history.push('/portal-updates');
    } catch (err) {
      notification.error({ message: 'Save failed', description: err.response?.data?.message || err.message });
    } finally { setSaving(false); }
  };

  return (
    <div className="layout-content">
      <Card
        title={
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => history.push('/portal-updates')} />
            <Title level={4} style={{ margin: 0, color: '#fff' }}>
              {isEdit ? 'Edit Portal Update' : 'New Portal Update'}
            </Title>
          </Space>
        }
        headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
        loading={loading}
      >
        <Row gutter={[24, 0]}>
          {/* ── Left: Form ── */}
          <Col xs={24} lg={14}>
            <Form form={form} layout="vertical" onFinish={handleSave}
              initialValues={{ is_published: true }}>

              <Form.Item name="title" label="शीर्षक (Title)"
                rules={[{ required: true, message: 'Title आवश्यक है' }]}>
                <Input placeholder="जैसे: गुरुकुल भवन का नींव कार्य प्रारंभ" maxLength={255} />
              </Form.Item>

              <Row gutter={[12, 0]}>
                <Col xs={24} sm={14}>
                  <Form.Item name="category_id" label="श्रेणी (Category)">
                    <Select placeholder="श्रेणी चुनें" allowClear
                      dropdownRender={menu => (
                        <>
                          {menu}
                          <div style={{ padding: '6px 12px', borderTop: '1px solid #f0f0f0', fontSize: 12, color: '#888' }}>
                            श्रेणी प्रबंधन के लिए List पेज पर जाएं → Manage Categories
                          </div>
                        </>
                      )}>
                      {categories.map(c => (
                        <Select.Option key={c.id} value={String(c.id)}>
                          <span style={{ color: c.color, fontWeight: 600 }}>{c.name}</span>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={10}>
                  <Form.Item name="post_date" label="दिनांक (Date)">
                    <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" placeholder="दिनांक चुनें" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="content" label="विवरण / नोट्स">
                <TextArea rows={7}
                  placeholder="विस्तृत जानकारी यहाँ लिखें — जैसे: किसे सहायता दी, कितनी राशि, क्या कार्य हुआ..."
                  maxLength={3000} showCount />
              </Form.Item>

              <Form.Item name="is_published" label="Portal पर दिखाएं?" valuePropName="checked">
                <Switch checkedChildren="Published" unCheckedChildren="Draft" />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}
                    style={{ background: '#7a0000', borderColor: '#7a0000' }}>
                    {isEdit ? 'Save Changes' : 'Publish Update'}
                  </Button>
                  <Button onClick={() => history.push('/portal-updates')}>Cancel</Button>
                </Space>
              </Form.Item>
            </Form>
          </Col>

          {/* ── Right: Image ── */}
          <Col xs={24} lg={10}>
            <div style={{ borderLeft: '1px solid #f0f0f0', paddingLeft: 24 }}>
              <Title level={5} style={{ marginBottom: 12 }}>
                <CameraOutlined style={{ marginRight: 6, color: '#7a0000' }} />
                फोटो (optional)
              </Title>

              <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => handleImageFile(e.target.files[0])} />

              <div
                onClick={() => !uploading && inputRef.current?.click()}
                style={{
                  width: '100%', minHeight: 200, borderRadius: 10,
                  border: `2px dashed ${preview ? '#7a0000' : '#d9d9d9'}`,
                  background: '#fafafa', cursor: 'pointer', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginBottom: 10,
                }}
              >
                {preview ? (
                  <img src={preview} alt="preview"
                    style={{ width: '100%', objectFit: 'cover', display: 'block' }} />
                ) : uploading ? (
                  <div style={{ textAlign: 'center', color: '#888' }}>
                    <LoadingOutlined style={{ fontSize: 32 }} />
                    <div style={{ marginTop: 8, fontSize: 12 }}>Uploading...</div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: '#bbb', padding: 24 }}>
                    <CameraOutlined style={{ fontSize: 40, display: 'block', marginBottom: 8 }} />
                    <Text style={{ fontSize: 13, color: '#aaa' }}>क्लिक करें या फोटो यहाँ ड्रॉप करें</Text>
                    <div style={{ fontSize: 11, color: '#ccc', marginTop: 4 }}>JPG, PNG, WebP · max 10MB</div>
                  </div>
                )}
              </div>

              <Space>
                <Button icon={<CameraOutlined />} onClick={() => inputRef.current?.click()} loading={uploading}>
                  {preview ? 'फोटो बदलें' : 'फोटो चुनें'}
                </Button>
                {preview && (
                  <Button danger icon={<DeleteOutlined />} onClick={() => { setPreview(null); setImageUrl(null); }}>
                    हटाएं
                  </Button>
                )}
              </Space>

              <Alert type="info" showIcon style={{ marginTop: 16, fontSize: 12 }}
                message="फोटो User Portal के गतिविधि Feed में दिखेगी।"
                description="निर्माण कार्य, सहायता कार्यक्रम, गुरुकुल की फोटो यहाँ जोड़ें।" />
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
}
